// Components — docs/ARCHITECTURE.md §4. Rules (game-engine/constrains/esengine-coding-constrain.md):
// pure data, primitives only, every field initialized, SimId numbers instead of entity references.
// `@sync` marks what views carry; private player data is never @sync (it travels on the private channel).
// Requires tsconfig `useDefineForClassFields: false`: @sync installs prototype accessors.

import { Component, ECSComponent, Serializable, Serialize, sync, type ComponentType } from '@esengine/ecs-framework'

@ECSComponent('SimIdentity')
@Serializable({ version: 1, typeId: 'SimIdentity' })
export class SimIdentity extends Component {
  @Serialize() @sync('uint32') simId = 0
}

@ECSComponent('Owner')
@Serializable({ version: 1, typeId: 'Owner' })
export class Owner extends Component {
  /** OwnerValue: a seat (PlayerId) or NEUTRAL_OWNER (-1). */
  @Serialize() @sync('int8') player = -1
}

@ECSComponent('GeoPosition')
@Serializable({ version: 1, typeId: 'GeoPosition' })
export class GeoPosition extends Component {
  @Serialize() @sync('float32') lat = 0
  @Serialize() @sync('float32') lon = 0
  /** Radians clockwise from north. */
  @Serialize() @sync('float32') heading = 0
}

@ECSComponent('GraphTraversal')
@Serializable({ version: 1, typeId: 'GraphTraversal' })
export class GraphTraversal extends Component {
  /** CityIndex values. The client interpolates GeoPosition instead of these. */
  @Serialize() fromCity = 0
  @Serialize() toCity = 0
  /** 0..1 progress along the edge. */
  @Serialize() t = 0
  /** DomainCode of the edge being travelled. */
  @Serialize() domain = 0
  @Serialize() @sync('boolean') convoy = false
}

@ECSComponent('Health')
@Serializable({ version: 1, typeId: 'Health' })
export class Health extends Component {
  @Serialize() @sync('float32') hp = 0
  @Serialize() @sync('float32') maxHp = 0
}

@ECSComponent('CityNode')
@Serializable({ version: 1, typeId: 'CityNode' })
export class CityNode extends Component {
  /** CityIndex in the WarMap. */
  @Serialize() @sync('uint16') cityIndex = 0
  @Serialize() @sync('boolean') isCapital = false
  @Serialize() @sync('boolean') coastal = false
  @Serialize() @sync('boolean') fallen = false
  /** 0..1 capture progress while fallen. */
  @Serialize() @sync('float32') captureProgress = 0
  /** OwnerValue of the side currently capturing. */
  @Serialize() @sync('int8') captureBy = -1
  @Serialize() buildingSlots = 0
}

@ECSComponent('Building')
@Serializable({ version: 1, typeId: 'Building' })
export class Building extends Component {
  /** BuildingTypeId. */
  @Serialize() @sync('uint8') typeId = 0
  @Serialize() @sync('uint32') citySimId = 0
}

@ECSComponent('UnitKind')
@Serializable({ version: 1, typeId: 'UnitKind' })
export class UnitKind extends Component {
  /** UnitTypeId. */
  @Serialize() @sync('uint8') typeId = 0
  /** Living bodies (squads/swarms lose bodies as they take damage). */
  @Serialize() @sync('uint8') bodies = 0
}

@ECSComponent('Weapon')
@Serializable({ version: 2, typeId: 'Weapon' })
export class Weapon extends Component {
  @Serialize() damage = 0
  /** Seconds between attacks. */
  @Serialize() period = 0
  @Serialize() range = 0
  @Serialize() minRange = 0
  @Serialize() splash = 0
  /** TargetClassCode. */
  @Serialize() targetClass = 0
  /** Ticks until the next attack is ready. Owned by WeaponSystem. */
  @Serialize() cooldownTicks = 0
  /** SimId of the locked target; 0 = none. Written only by behaviour-tree executors. */
  @Serialize() @sync('uint32') lockedTarget = 0
  /**
   * Attacks made, wrapping at SHOT_COUNTER_WRAP. Written only by WeaponSystem, once per attack; the client plays one
   * attack effect per advance (it changes only when the weapon fires, so it costs no bandwidth otherwise).
   */
  @Serialize() @sync('uint8') shots = 0
}

/** Weapon.shots wraps here (it is synced as a uint8). */
export const SHOT_COUNTER_WRAP = 256

@ECSComponent('Mobility')
@Serializable({ version: 1, typeId: 'Mobility' })
export class Mobility extends Component {
  /** mu per second. */
  @Serialize() speed = 0
  /** DomainCode the unit moves in. */
  @Serialize() domain = 0
  /** CityIndex the unit is heading for; -1 = none. Written only by behaviour-tree executors. */
  @Serialize() goalCity = -1
  /** SimId the unit pursues directly (air units / in-sight pursuit); 0 = none. */
  @Serialize() goalTarget = 0
}

@ECSComponent('Spawner')
@Serializable({ version: 1, typeId: 'Spawner' })
export class Spawner extends Component {
  /** UnitTypeId spawned. */
  @Serialize() typeId = 0
  @Serialize() intervalTicks = 0
  @Serialize() maxAlive = 0
  @Serialize() ticksUntilNext = 0
}

@ECSComponent('Sight')
@Serializable({ version: 1, typeId: 'Sight' })
export class Sight extends Component {
  /** mu. */
  @Serialize() radius = 0
}

@ECSComponent('StrikeFlight')
@Serializable({ version: 2, typeId: 'StrikeFlight' })
export class StrikeFlight extends Component {
  /** StrikeTypeId. */
  @Serialize() @sync('uint8') typeId = 0
  /** Where it is headed: its homing target's position this tick, else the point it was aimed at. */
  @Serialize() @sync('float32') targetLat = 0
  @Serialize() @sync('float32') targetLon = 0
  /** The launch city. */
  @Serialize() @sync('float32') originLat = 0
  @Serialize() @sync('float32') originLon = 0
  /** 1 once it reached its target (it rests there for its remaining salvos); 0 while it flies. */
  @Serialize() @sync('uint8') onTarget = 0
  /** Ticks from arrival to the pending salvo; they count down only once on target (salvo 0 lands on arrival). */
  @Serialize() ticksLeft = 0
  /** 0-based salvo index for multi-salvo strikes. */
  @Serialize() @sync('uint8') salvo = 0
  /** SimId of the entity it homes on; 0 = none (it flies to a point, or its target is gone). Sim only. */
  @Serialize() homingTarget = 0
}

@ECSComponent('Demoralized')
@Serializable({ version: 1, typeId: 'Demoralized' })
export class Demoralized extends Component {
  @Serialize() untilTick = 0
  @Serialize() damageMultiplier = 1
}

/** Private: sent only to its owner through PlayerPrivateState. */
@ECSComponent('PlayerEconomy')
@Serializable({ version: 1, typeId: 'PlayerEconomy' })
export class PlayerEconomy extends Component {
  @Serialize() player = 0
  @Serialize() money = 0
  /** Last computed revenue per second (after tax, approval and sanctions). */
  @Serialize() revenuePerSec = 0
  /** TaxCode. */
  @Serialize() tax = 2
  @Serialize() approval = 0
  @Serialize() sanctionedUntilTick = 0
  @Serialize() sanctionRevenueMultiplier = 1
}

/** Private: War Ledger (GDD §6). */
@ECSComponent('Ledger')
@Serializable({ version: 1, typeId: 'Ledger' })
export class Ledger extends Component {
  @Serialize() player = 0
  @Serialize() spentUnits = 0
  @Serialize() spentBuildings = 0
  @Serialize() spentStrikes = 0
  @Serialize() lostValue = 0
  @Serialize() citiesCaptured = 0
  @Serialize() citiesLost = 0
}

/** Singleton on the match entity. Per-seat host state (last command seq) lives on each seat's private entity. */
@ECSComponent('MatchState')
@Serializable({ version: 2, typeId: 'MatchState' })
export class MatchState extends Component {
  @Serialize() @sync('uint32') tick = 0
  /** MatchPhase. */
  @Serialize() @sync('uint8') phase = 0
  @Serialize() @sync('uint32') phaseEndTick = 0
  /** OutcomeKind. */
  @Serialize() @sync('uint8') outcome = 0
  /** OwnerValue of the winner; -1 while running or on a draw. */
  @Serialize() @sync('int8') winner = -1
  @Serialize() nextSimId = 1
  @Serialize() seed = 0
  /** xoshiro128** state. */
  @Serialize() rng0 = 0
  @Serialize() rng1 = 0
  @Serialize() rng2 = 0
  @Serialize() rng3 = 0
}

/** Every component class, in registration order. Register all before creating entities. */
export const ALL_COMPONENTS: readonly ComponentType<Component>[] = [
  SimIdentity,
  Owner,
  GeoPosition,
  GraphTraversal,
  Health,
  CityNode,
  Building,
  UnitKind,
  Weapon,
  Mobility,
  Spawner,
  Sight,
  StrikeFlight,
  Demoralized,
  PlayerEconomy,
  Ledger,
  MatchState,
]
